import ValidationType, { ValidationTypeName } from '../Enums/ValidationType';
import { getAsLongDate, getAsLongDateTime, getAsLongDateStartAndEnd, getAsTimeFormat } from '../../common-client2/src/services/ukgDateTime';

/**
 * Converts PascalCase keys to camelCase.
 * C# serializes with PascalCase (e.g. AllowedOverlapMinutes) but
 * translation placeholders use camelCase (e.g. {{allowedOverlapMinutes}}).
 */
const toCamelCaseKeys = (obj) => {
    if (obj === null || typeof obj !== 'object' || Array.isArray(obj)) return obj;
    return Object.keys(obj).reduce((acc, key) => {
        const camelKey = key.charAt(0).toLowerCase() + key.slice(1);
        acc[camelKey] = obj[key];
        return acc;
    }, {});
};

/**
 * Formats a number of minutes as an hh:mm duration, matching how durations are entered in the rule-set setup.
 */
const formatMinutesAsDuration = (totalMinutes) => {
    const rounded = Math.round(totalMinutes);
    const hours = Math.floor(rounded / 60);
    const minutes = `0${rounded % 60}`.slice(-2);
    return `${hours}:${minutes}`;
};

/**
 * Enriches the context with pre-formatted date/time values
 * so translations can reference e.g. {{overlapStartFormatted}}.
 */
const enrichContextWithFormattedDates = (context) => {
    const dateTimeFields = [
        'overlapStart', 'overlapEnd',
        'paycodeStartDateTime', 'paycodeEndDateTime',
        'invalidRangeStart', 'invalidRangeEnd',
    ];
    const dateFields = [
        'probationEndDate',
        'weekStartDate', 'weekEndDate',
        'periodStartDate', 'periodEndDate',
        'shiftDate', 'shiftStartDate',
        'restrictionStartDate', 'restrictionEndDate',
    ];

    dateTimeFields.forEach(field => {
        if (context[field]) {
            context[field + 'Formatted'] = getAsLongDateTime(context[field]);
        }
    });
    dateFields.forEach(field => {
        if (context[field]) {
            context[field + 'Formatted'] = getAsLongDate(context[field]);
        }
    });

    // Format InvalidOverlapShifts: use first shift for specific conflict display
    if (Array.isArray(context.invalidOverlapShifts) && context.invalidOverlapShifts.length > 0) {
        const firstOverlap = context.invalidOverlapShifts[0];
        const firstStart = firstOverlap.StartDateTime || firstOverlap.startDateTime;
        const firstEnd = firstOverlap.EndDateTime || firstOverlap.endDateTime;
        context.conflictingShiftStartFormatted = getAsLongDateTime(firstStart);
        context.conflictingShiftEndFormatted = getAsTimeFormat(firstEnd);
        // Keep summary for RestBetweenShifts (uses all shifts)
        context.conflictingShiftsSummary = context.invalidOverlapShifts
            .map(s => getAsLongDateStartAndEnd(s.StartDateTime || s.startDateTime, s.EndDateTime || s.endDateTime))
            .join('; ');
    }

    // Format InvalidShifts (rest between shifts) into a readable string
    if (Array.isArray(context.invalidShifts) && context.invalidShifts.length > 0) {
        context.conflictingShiftsSummary = context.invalidShifts
            .map(s => getAsLongDateStartAndEnd(s.StartDateTime || s.startDateTime, s.EndDateTime || s.endDateTime))
            .join('; ');
    }

    // Round numeric values for cleaner display
    if (typeof context.workedHours === 'number') {
        context.workedHours = Math.round(context.workedHours * 100) / 100;
    }
    if (typeof context.maxHours === 'number') {
        context.maxHours = Math.round(context.maxHours * 100) / 100;
    }
    if (typeof context.overlapMinutes === 'number') {
        context.overlapMinutes = Math.round(context.overlapMinutes * 100) / 100;
    }
    if (typeof context.requiredRestMinutes === 'number') {
        context.requiredRestHours = Math.round((context.requiredRestMinutes / 60) * 100) / 100;
    }

    // Consecutive hours limits and actuals are sent in minutes and displayed as hh:mm
    if (typeof context.consecutiveMinutesActual === 'number') {
        context.consecutiveDurationFormatted = formatMinutesAsDuration(context.consecutiveMinutesActual);
    }
    if (typeof context.minConsecutiveMinutesLimit === 'number') {
        context.minConsecutiveDurationFormatted = formatMinutesAsDuration(context.minConsecutiveMinutesLimit);
    }
    if (typeof context.maxConsecutiveMinutesLimit === 'number') {
        context.maxConsecutiveDurationFormatted = formatMinutesAsDuration(context.maxConsecutiveMinutesLimit);
    }

    return context;
};

/**
 * Formats a structured validation failure detail into a localized message.
 *
 * @param {Object} failureDetail - The structured failure detail from the backend
 * @param {string} failureDetail.validationName - The validation type name
 * @param {number} failureDetail.validationType - The validation type ID
 * @param {string} failureDetail.jsonExtraDetails - JSON string with context-specific details
 * @param {Function} t - i18next translation function
 * @returns {string} A localized, human-readable failure message
 */
/**
 * Resolves the translation key(s) for rules that have conditional sub-keys.
 * Returns an array of keys to try in order, or multiple messages for ScheduleRules.
 */
const resolveTranslationKeys = (keyName, context) => {
    switch (keyName) {
        case ValidationTypeName[ValidationType.AllowedTimeBeforeShift]:
            return [context.isAfterShift
                ? 'validationFailureDetails.AllowedTimeBeforeShift_after'
                : 'validationFailureDetails.AllowedTimeBeforeShift_before'];

        case ValidationTypeName[ValidationType.ScheduleRules]: {
            // If exactly one sub-rule fails, show the specific message; otherwise use fallback
            const subRuleKeys = [];
            if (context.maxHoursPerDayActual != null) subRuleKeys.push('validationFailureDetails.ScheduleRules_maxHoursPerDay');
            if (context.maxHoursPerWeekActual != null) subRuleKeys.push('validationFailureDetails.ScheduleRules_maxHoursPerWeek');
            if (context.maxShiftsPerDayActual != null) subRuleKeys.push('validationFailureDetails.ScheduleRules_maxShiftsPerDay');
            if (context.maxConsecutiveDaysActual != null) subRuleKeys.push('validationFailureDetails.ScheduleRules_maxConsecutiveDays');
            return subRuleKeys.length > 0 ? subRuleKeys : ['validationFailureDetails.ScheduleRules_fallback'];
        }

        case ValidationTypeName[ValidationType.MaxBiWeeklyHours]:
            return ['validationFailureDetails.MaxBiWeeklyHours'];

        case ValidationTypeName[ValidationType.ConsecutiveHours]: {
            const messages = [];
            if (context.maxConsecutiveMinutesLimit > 0 && context.consecutiveMinutesActual > context.maxConsecutiveMinutesLimit)
                messages.push('validationFailureDetails.ConsecutiveHours_max');
            if (context.minConsecutiveMinutesLimit > 0 && context.consecutiveMinutesActual < context.minConsecutiveMinutesLimit)
                messages.push('validationFailureDetails.ConsecutiveHours_min');
            return messages.length > 0 ? messages : ['validationFailureDetails.ConsecutiveHours_fallback'];
        }

        case ValidationTypeName[ValidationType.JobTransferSetValidation]:
            return [context.jobTransferSetName
                ? 'validationFailureDetails.JobTransferSetValidation_withJts'
                : 'validationFailureDetails.JobTransferSetValidation_primaryOnly'];

        case ValidationTypeName[ValidationType.MultipleShiftOnSameDay]: {
            const messages = [];
            if (context.actualConsecutiveDays != null && context.actualConsecutiveDays > context.maxConsecutiveDays)
                messages.push('validationFailureDetails.ShiftsBackToBack_consecutiveDays');
            if (context.actualPerWeek != null && context.actualPerWeek > context.maxPerWeek)
                messages.push('validationFailureDetails.ShiftsBackToBack_perWeek');
            return messages.length > 0 ? messages : ['validationFailureDetails.ShiftsBackToBack_fallback'];
        }

        case ValidationTypeName[ValidationType.SameDuration]: {
            return ['validationFailureDetails.SameDuration'];
        }

        case ValidationTypeName[ValidationType.SwapPeriod]: {
            // One SwapPeriod rule with one selected window; the window comes through on the extra details.
            const subKey = context.swapPeriodType
                ? `validationFailureDetails.SwapPeriod_${context.swapPeriodType}`
                : 'validationFailureDetails.SwapPeriod';
            return [subKey];
        }

        default:
            return [`validationFailureDetails.${keyName}`];
    }
};

export const formatValidationFailure = (failureDetail, t) => {
    const { validationName, validationType, jsonExtraDetails } = failureDetail;
    let context = {};

    if (jsonExtraDetails) {
        try {
            const parsed = JSON.parse(jsonExtraDetails);
            context = toCamelCaseKeys(parsed) || {};
        } catch (e) {
            // If JSON parsing fails, fall back to validation name only
        }
    }

    // Add derived labels
    if (context.periodUnit != null) {
        context.periodUnitLabel = context.periodUnit === 0 ? 'hours' : 'days';
    }
    if (typeof context.ruleType === 'number') {
        const ruleTypeKey = context.ruleType === 0
            ? 'validationFailureDetails.skillsAndCertifsRuleType.skill'
            : 'validationFailureDetails.skillsAndCertifsRuleType.certification';
        context.ruleTypeLabel = t(ruleTypeKey);
    }

    context = enrichContextWithFormattedDates(context);

    // Use the numeric validationType to look up the enum name for the translation key
    const keyName = ValidationTypeName[validationType] || validationName;
    const keys = resolveTranslationKeys(keyName, context);

    const results = keys.map(key => {
        let hasMissingPlaceholder = false;
        const translated = t(key, {
            ...context,
            defaultValue: '',
            missingInterpolationHandler: () => { hasMissingPlaceholder = true; return ''; },
        });
        if (!translated || translated === key) return null;
        // If the template referenced a placeholder not present in the context,
        // the data is incomplete (e.g. old records without enriched details).
        // Fall back to validationName instead of showing a broken sentence.
        if (hasMissingPlaceholder) return null;
        return translated;
    }).filter(Boolean);

    if (results.length > 0) return results.join('. ');

    return validationName || `Unknown validation (${validationType})`;
};

/**
 * Formats a list of structured validation failure details into localized messages.
 *
 * @param {Object} historyItem - The history item containing validation data
 * @param {Function} t - i18next translation function
 * @returns {string[]} Array of localized failure messages
 */
export const getValidationFailureMessages = (historyItem, t) => {
    if (historyItem.validationFailureDetails && historyItem.validationFailureDetails.length > 0) {
        return historyItem.validationFailureDetails.map(detail => formatValidationFailure(detail, t));
    }

    return [];
};

